import { CartService } from '../../../core/services/cart/cart.service';
import { ResponsiveService } from '../../../core/services/utils/responsive.service';
import { ShopSelectionStore } from '../../../core/state/shop-selection.store';
import {
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Renderer2,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { DetailedProduct, Product } from '../../models/product.model';
import { MatIconModule } from '@angular/material/icon';
import { Resident } from '../../models/resident.model';
import { CommonModule, registerLocaleData } from '@angular/common';
import { MatDivider, MatDividerModule } from '@angular/material/divider';
import { ProductCounterComponent } from '../product-card/product-counter/product-counter.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProfileAvatarComponent } from '../profile-avatar/profile-avatar.component';
import * as de from '@angular/common/locales/de';
import { InactiveResidentAlertComponent } from '../inactive-resident-alert/inactive-resident-alert.component';
import { filter, firstValueFrom } from 'rxjs';
import { ProductsService } from '../../../core/services/products';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AnalyticsService } from '../../../core/services/analytics/analytics.service';
import { DepartmentWithResidents } from '../../models/department.model';
import { NoConnextAccountIdErrorComponent } from '../no-connext-account-id-error/no-connext-account-id-error.component';
import { ModalCloseButtonComponent } from '../modal-close-button/modal-close-button.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ZeroPriceGuardService } from '../../../core/services/zero-price-guard/zero-price-guard.service';
import {
  UNRESTRICTED_COUNTER,
  ZeroPriceCounterState,
} from '../../../core/services/zero-price-guard/zero-price.util';
import { HasNoValidPaymentMethodPipe } from '../../pipes/has-no-valid-payment-method.pipe';

@Component({
  selector: 'app-product-modal',
  templateUrl: './product-modal.component.html',
  styleUrls: ['./product-modal.component.scss'],
  imports: [
    HasNoValidPaymentMethodPipe,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    CommonModule,
    MatDividerModule,
    ProductCounterComponent,
    FormsModule,
    ReactiveFormsModule,
    ProfileAvatarComponent,
    InactiveResidentAlertComponent,
    MatProgressSpinnerModule,
    NoConnextAccountIdErrorComponent,
    ModalCloseButtonComponent,
    MatExpansionModule,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
  ],
})
export class ProductModalComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  selectedResident: Resident | null = null;
  product: DetailedProduct;
  selectedDepartment: DepartmentWithResidents | null = null;
  residents: Resident[] = [];
  isMobile = false;
  @Input() counter = 0;
  formControl: FormControl = new FormControl(0);
  originalContent: string = '';
  truncatedContent: string = '';
  fullContent: string = '';
  showFullContent = false;
  showChar = 190;
  ellipses = '... ';
  mockDescription: string =
    'Produktbeschreibung folgt in Kürze! Dieser Platzhaltertext markiert, wo später detaillierte Informationen zum Produkt erscheinen werden, einschließlich seiner Eigenschaften, Vorteile und Anwendungshinweise. Bald gibt es hier die vollständige Beschreibung, um mehr darüber zu erfahren, wie dieses Produkt Ihren Bedürfnissen gerecht werden kann.';

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { productId: number },
    private dialogRef: MatDialogRef<ProductModalComponent>,
    private shopSelectionStore: ShopSelectionStore,
    private responsiveService: ResponsiveService,
    private cartService: CartService,
    private productsService: ProductsService,
    private zeroPriceGuardService: ZeroPriceGuardService,
    private analyticsService: AnalyticsService
  ) {
    registerLocaleData(de.default);
    this.responsiveService.isMobile$
      .pipe(takeUntilDestroyed())
      .subscribe(isMobile => {
        this.isMobile = isMobile;
      });
    this.shopSelectionStore.resident$
      .pipe(takeUntilDestroyed())
      .subscribe(resident => {
        this.selectedResident = resident;
      });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  async ngOnInit(): Promise<void> {
    if (!this.selectedResident) {
      const response = await firstValueFrom(
        this.productsService.getProductDetails(this.data.productId)
      );
      this.product = response.data;
      this.analyticsService.trackViewItem(this.product);
      this.refreshZeroPriceLimits();
      this.originalContent = (
        this.product.description || this.mockDescription
      ).trim();
      this.setTruncatedText(this.originalContent);
    } else {
      this.shopSelectionStore.department$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async department => {
          if (!department) return;
          this.residents = department.people.filter(
            resident => resident.id !== this.selectedResident.id
          );
          const response = await firstValueFrom(
            this.productsService.getProductDetails(
              this.data.productId,
              department.people.map(resident => resident.id)
            )
          );
          this.product = response.data;
          this.analyticsService.trackViewItem(this.product);
          this.refreshZeroPriceLimits();
          this.refreshZeroPriceLimits();
          this.originalContent = (
            this.product.description || this.mockDescription
          ).trim();
          this.setTruncatedText(this.originalContent);
        });

      this.counter =
        this.cartService.cart
          .getResidentCart(this.selectedResident.id)
          ?.getProduct(this.data.productId)?.quantity || 0;

      this.formControl.setValue(this.counter);
    }

    this.selectedDepartment = await firstValueFrom(
      this.shopSelectionStore.department$
    );
  }

  private setTruncatedText(description: string): void {
    this.originalContent = description;

    const textContent = this.stripHtmlTags(description);

    if (textContent.length > this.showChar) {
      const truncatedText = textContent.slice(0, this.showChar);
      this.truncatedContent =
        `<p class="truncated-text">${truncatedText}${this.ellipses}</p>`.replace(
          /&nbsp;/g,
          ' '
        );
      this.fullContent = description + ' ';
    } else {
      this.truncatedContent = description;
    }
  }

  private stripHtmlTags(html: string): string {
    return html.replace(/<[^>]*>/g, '');
  }

  toggleText(event: Event): void {
    event.preventDefault();
    this.showFullContent = !this.showFullContent;
  }

  increment() {
    this.counter++;
  }

  decrement() {
    if (this.counter > 0) {
      this.counter--;
    }
  }
  onAddToCartClick() {
    let residentCart = this.cartService.cart.getResidentCart(
      this.selectedResident.id
    );
    this.cartService.setUpdate();
    if (!residentCart) {
      residentCart = this.cartService.cart.createNewResidentCart(
        this.selectedResident.id
      );
    }
    if ((this.isMobile ? this.formControl.value : this.counter) === 0) {
      this.cartService.cart.removeProductInResidentCart(
        this.selectedResident.id,
        this.data.productId
      );
      return;
    }
    if (!residentCart.getProduct(this.data.productId)) {
      this.cartService.cart.addProductToResidentCart(
        this.selectedResident.id,
        this.product.convertToCartProduct()
      );
    }

    this.cartService.cart.changeResidentCartProductQuantity(
      this.selectedResident.id,
      this.data.productId,
      this.isMobile ? this.formControl.value : this.counter
    );
  }

  onCounterChange(event): void {
    let residentCart = this.cartService.cart.getResidentCart(event.resident.id);
    this.cartService.setUpdate();
    if (!residentCart) {
      residentCart = this.cartService.cart.createNewResidentCart(
        event.resident.id
      );
    }
    if (event.value === 0) {
      this.cartService.cart.removeProductInResidentCart(
        event.resident.id,
        this.data.productId
      );
      return;
    }
    if (!residentCart.getProduct(this.data.productId)) {
      this.cartService.cart.addProductToResidentCart(
        event.resident.id,
        this.product.convertToCartProduct()
      );
    }

    this.cartService.cart.changeResidentCartProductQuantity(
      event.resident.id,
      this.data.productId,
      event.value
    );
    this.refreshZeroPriceLimits();
    if (event.resident.id !== this.selectedResident.id) {
      this.analyticsService.trackAddToCart(this.product, event.value, {
        item_list_name: 'cross_sell_residents',
      });
    }
  }

  getProductCount(resident: Resident) {
    if (
      resident &&
      this.cartService.cart.getResidentCart(resident.id) &&
      this.cartService.cart
        .getResidentCart(resident.id)
        .getProduct(this.data.productId)
    )
      return this.cartService.cart
        .getResidentCart(resident.id)
        .getProduct(this.data.productId).quantity;

    return 0;
  }

  isFavouriteForSelectedResident() {
    return !!this.selectedResident?.favouriteProducts?.includes(
      this.product.id
    );
  }

  isFavourite(residentId: number) {
    return !!this.product.favouriteResidentsIds?.includes(residentId);
  }

  connextError(resident: Resident) {
    return (
      this.selectedDepartment?.partnerDepartmentId &&
      !resident?.partnerAccountId &&
      resident?.hasIBAN === 0
    );
  }

  private zeroPriceStates = new Map<number, ZeroPriceCounterState>();

  /**
   * What one resident's counter is bound to. The stored object keeps `canIncrease` at a stable
   * identity across change detection; `maxQuantity` is refreshed whenever the cart moves.
   */
  zeroPriceState(resident: Resident): ZeroPriceCounterState {
    const residentId = resident?.id;
    if (residentId === null || residentId === undefined) {
      return UNRESTRICTED_COUNTER;
    }

    let state = this.zeroPriceStates.get(residentId);
    if (!state) {
      state = {
        maxQuantity: null,
        canIncrease: (currentValue: number, nextValue: number) =>
          this.zeroPriceGuardService.canIncrease({
            product: this.product,
            resident,
            currentValue,
            nextValue,
          }),
      };
      this.zeroPriceStates.set(residentId, state);
      this.refreshZeroPriceLimit(state, resident);
    }

    return state;
  }

  private refreshZeroPriceLimit(
    state: ZeroPriceCounterState,
    resident: Resident
  ): void {
    state.maxQuantity = this.zeroPriceGuardService.maxQuantityFor({
      product: this.product,
      resident,
    });
  }

  private refreshZeroPriceLimits(): void {
    this.zeroPriceStates.forEach((state, residentId) => {
      const resident =
        this.selectedResident?.id === residentId
          ? this.selectedResident
          : this.residents.find(person => person.id === residentId);
      this.refreshZeroPriceLimit(state, resident);
    });
  }

  get quantityLimitText(): string {
    const maxQuantity = this.zeroPriceState(this.selectedResident).maxQuantity;

    return maxQuantity === null ? '' : `Max. ${maxQuantity} Stück`;
  }
}
